import { AxiosError } from 'axios'
import { ArrowRight } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { PasswordInput } from '@/Components/PasswordInput'

import { useGetNewPassword } from '../../../api/auth/mutations/useGetNewPassword'
import { useUpdateForgetPassword } from '../../../api/auth/mutations/useUpdateForgetPassword'
import { TextField } from '../../../Components/TextField'
import { Button } from '../../../Components/ui/button'
import { RoutePath } from '../../../RoutePath'

export default function ForgotPasswordPage() {
  const history = useHistory()
  const updateForgetPasswordMutation = useUpdateForgetPassword()
  const getNewPasswordMutation = useGetNewPassword()
  const [formState, setFormState] = useState({
    username: '',
    forgotPasswordNewPassword: '',
    forgotPasswordNewPassword2: '',
    forgotPasswordConfirmationCode: ['', '', '', '', '', ''],
    formType: 'forgotPassword',
  })
  const [errorMessage, setErrorMessage] = useState('')
  const [newPasswordError, setNewPasswordError] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getTextError = (error: any) => {
    if (error?.response?.data?.error?.code == 'CodeMismatchException') return 'הקוד שהוזן אינו תקין'
    else return 'שגיאה! אנא בדוק שכל הנתונים נכונים ונסה שנית'
  }
  const backToSignIn = () => {
    history.push(RoutePath.Login)
  }
  const forgotPasswordConfirmation = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    try {
      setErrorMessage('')
      setNewPasswordError('')
      if (formState.forgotPasswordNewPassword !== formState.forgotPasswordNewPassword2) {
        setNewPasswordError('הסיסמאות שהכנסת לא זהות')
        return
      }
      if (formState.forgotPasswordNewPassword.length < 8) {
        setNewPasswordError('על הסיסמה לכלול לפחות 8 תווים ואות באנגלית')
        return
      }
      if (!/[a-zA-Z]/.test(formState.forgotPasswordNewPassword)) {
        setNewPasswordError('על הסיסמה לכלול אות באנגלית')
        return
      }

      const { username, forgotPasswordNewPassword, forgotPasswordConfirmationCode } = formState
      // Join the code array directly (index 0 is leftmost visually, so no reversal needed)
      await updateForgetPasswordMutation.mutateAsync({
        username,
        forgotPasswordConfirmationCode: forgotPasswordConfirmationCode.join(''),
        forgotPasswordNewPassword,
      })
      backToSignIn()
    } catch (_error) {
      const error = _error as AxiosError<{ errorCode?: string; msg?: string }>
      if (error?.response?.data?.errorCode == 'CodeMismatchException') {
        setFormState(prev => ({
          ...prev,
          forgotPasswordNewPassword: '',
          forgotPasswordNewPassword2: '',
          formType: 'forgotPasswordConfirmation',
        }))
      }
      setErrorMessage(error?.response?.data?.msg || getTextError(error))
    }
  }

  const sendConfirmCode = async () => {
    try {
      setErrorMessage('')
      const { username } = formState
      await getNewPasswordMutation.mutateAsync(username)

      setFormState(prev => ({
        ...prev,
        formType: 'forgotPasswordConfirmation',
      }))

      // Start 30 second cooldown
      setResendCooldown(30)
    } catch (error) {
      console.log('forgotPassword - error', error)
      setErrorMessage('משתמש לא נמצא')
    }
  }

  const resendConfirmCode = async () => {
    if (resendCooldown > 0) return
    setFormState(prev => ({
      ...prev,
      forgotPasswordConfirmationCode: ['', '', '', '', '', ''],
      forgotPasswordNewPassword: '',
      forgotPasswordNewPassword2: '',
    }))

    try {
      setErrorMessage('')
      const { username } = formState
      await getNewPasswordMutation.mutateAsync(username)
      // Start 30 second cooldown
      setResendCooldown(30)
    } catch (error) {
      console.log('resendConfirmCode - error', error)
      setErrorMessage('שגיאה בשליחת הקוד. אנא נסה שנית')
    }
  }

  // Countdown timer effect
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(prev => prev - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  // Reset cooldown when form type changes back to forgotPassword
  useEffect(() => {
    if (formState.formType === 'forgotPassword') {
      setResendCooldown(0)
    }
  }, [formState.formType])

  const { formType } = formState
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist()
    const { name, value } = e.target
    setFormState(prev => ({ ...prev, [name]: value }))
  }
  const handleConfirmationCodeChange = (value: string, index: number) => {
    // Only allow single digit input
    const digit = value.slice(-1)

    setFormState(prev => ({
      ...prev,
      forgotPasswordConfirmationCode: [
        ...prev.forgotPasswordConfirmationCode.slice(0, index),
        digit,
        ...prev.forgotPasswordConfirmationCode.slice(index + 1),
      ],
    }))

    const nextIndex = index + 1
    // Auto-focus to next input if a digit was entered (move right visually in RTL)
    if (digit && nextIndex < 6) {
      inputRefs.current[nextIndex]?.focus()
      inputRefs.current[nextIndex]?.select()
    }
  }

  const handleConfirmationCodePaste = (e: React.ClipboardEvent, index: number) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)

    if (pastedData.length === 6) {
      // Fill all inputs with the pasted code in visual order (left to right)
      // Index 0 is visually leftmost, so assign directly
      const newCode = pastedData.split('')
      setFormState(prev => ({
        ...prev,
        forgotPasswordConfirmationCode: newCode,
      }))

      // Focus the rightmost input (index 5)
      inputRefs.current[5]?.focus()
    } else if (pastedData.length > 0) {
      // Handle partial paste - fill from the paste position forward (to the right visually)
      const newCode = [...formState.forgotPasswordConfirmationCode]

      // Fill starting from index, going forward
      for (let i = 0; i < pastedData.length && index + i < 6; i++) {
        newCode[index + i] = pastedData[i]
      }

      setFormState(prev => ({
        ...prev,
        forgotPasswordConfirmationCode: newCode,
      }))

      // Focus the next empty input or the rightmost one
      const nextEmptyIndex = newCode.findIndex((code, i) => i >= index && !code)
      const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 5
      inputRefs.current[focusIndex]?.focus()
    }
  }
  const onSubmitConfirmationCode = async () => {
    setErrorMessage('')
    setFormState(prev => ({
      ...prev,
      formType: 'newPassword',
    }))
  }
  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    // Handle backspace to go to previous input (move left visually in RTL)
    if (e.key === 'Backspace' && !formState.forgotPasswordConfirmationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  // Check if all confirmation code inputs are filled
  const isConfirmationCodeComplete = formState.forgotPasswordConfirmationCode.every(digit => digit !== '')
  return (
    <div dir='rtl' className='flex h-full items-center justify-center p-6'>
      <div className='flex w-full max-w-[480px] flex-col justify-center rounded-2xl bg-white p-6 shadow-sm'>
        {formType === 'forgotPassword' && (
          <div className='mb-8 text-center'>
            <h1 className='mb-4 text-lg font-medium text-gray-900'>שכחת סיסמה?</h1>
            <p className='text-sm leading-relaxed text-gray-600'>
              נשלח לך קוד למייל כדי לאפס את הסיסמה שלך.
              <br />
              יש לוודא שכתובת המייל זהה לכתובת שאיתה נרשמת לאתר.
            </p>
          </div>
        )}

        {/* Forgot Password Confirmation Title */}
        {formType === 'forgotPasswordConfirmation' && (
          <div className='mb-8 text-center'>
            <h1 className='mb-4 text-lg font-medium text-gray-900'>הזנת קוד אימות</h1>
            <p className='text-sm text-gray-600'>שלחנו לך קוד למייל {formState.username}</p>
          </div>
        )}

        {/* New Password Title */}
        {formType === 'newPassword' && (
          <div className='mb-8 text-center'>
            <h1 className='mb-4 text-lg font-medium text-gray-900'>הגדרת סיסמה חדשה</h1>
          </div>
        )}

        {errorMessage && (
          <div className='mb-6 rounded-lg border border-red-200 bg-red-50 p-4'>
            <p className='text-center text-sm text-red-600'>{errorMessage}</p>
          </div>
        )}

        {formType === 'forgotPassword' && (
          <div className='space-y-6'>
            <TextField label='כתובת מייל' type='email' value={formState.username} onChange={handleChange} name='username' />

            <Button onClick={sendConfirmCode} loading={getNewPasswordMutation.isPending} size='lg' className='w-full'>
              שלחו לי את הקוד
            </Button>

            <div className='text-center'>
              <Button onClick={backToSignIn} variant='link' size='sm' className='text-sm'>
                <ArrowRight className='h-4 w-4' />
                חזרה להתחברות
              </Button>
            </div>
          </div>
        )}

        {/* Forgot Password Confirmation Form */}
        {formType === 'forgotPasswordConfirmation' && (
          <div className='space-y-6'>
            <div dir='ltr' className='flex items-center justify-center gap-4'>
              {new Array(6).fill(0).map((_, index) => (
                <input
                  key={index}
                  ref={el => (inputRefs.current[index] = el)}
                  type='text'
                  maxLength={1}
                  value={formState.forgotPasswordConfirmationCode[index]}
                  className='h-12 w-full max-w-14 rounded-lg border border-gray-300 text-center text-lg font-medium outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
                  onChange={e => handleConfirmationCodeChange(e.target.value, index)}
                  onPaste={e => handleConfirmationCodePaste(e, index)}
                  onKeyDown={e => handleKeyDown(e, index)}
                />
              ))}
            </div>

            <Button
              onClick={onSubmitConfirmationCode}
              loading={updateForgetPasswordMutation.isPending}
              disabled={getNewPasswordMutation.isPending || !isConfirmationCodeComplete}
              size='lg'
              className='w-full'
            >
              הגדרת סיסמה חדשה
            </Button>

            <div className='flex items-center justify-center gap-1 text-center text-sm'>
              <span>{resendCooldown > 0 ? `לא קיבלתם קוד? שלחו שוב (${resendCooldown} שניות)` : 'לא קיבלתם קוד?'}</span>
              {!resendCooldown && (
                <Button
                  variant='link'
                  className='text-sm'
                  size='sm'
                  onClick={resendConfirmCode}
                  disabled={getNewPasswordMutation.isPending}
                >
                  שלחו שוב
                </Button>
              )}
            </div>
          </div>
        )}

        {/* New Password Form */}
        {formType === 'newPassword' && (
          <form onSubmit={forgotPasswordConfirmation} className='space-y-6'>
            <PasswordInput
              label='סיסמה חדשה'
              value={formState.forgotPasswordNewPassword}
              onChange={handleChange}
              name='forgotPasswordNewPassword'
              error={!!newPasswordError}
              supportingText={newPasswordError}
              autoComplete='new-password'
            />
            <PasswordInput
              label='אימות סיסמה'
              value={formState.forgotPasswordNewPassword2}
              onChange={handleChange}
              name='forgotPasswordNewPassword2'
              autoComplete='new-password webauthn'
            />

            <Button
              loading={updateForgetPasswordMutation.isPending}
              disabled={updateForgetPasswordMutation.isPending}
              size='lg'
              className='w-full'
            >
              שנה סיסמה
            </Button>
          </form>
        )}
      </div>
    </div>
  )
}
